import React, { ReactNode } from 'react';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SELECTED } from './selection';

/**
 * The parts of an editor's settings column — the left side of the Shopping
 * Assistant and Visualizer settings, beside their live canvas.
 *
 * Flat by design. The column is already a surface; a card inside it, and a
 * bordered box inside that card, only spend width and say nothing. Groups are
 * separated by space and a heading, fields by a grey label and a hint.
 */

/** A titled group of settings. `action` sits at the end of the heading row. */
export const SettingsGroup = ({
    title,
    description,
    action,
    children,
    className,
}: {
    title: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
    children: ReactNode;
    className?: string;
}) => (
    <section className={cn('flex flex-col gap-4', className)}>
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <h3 className="text-sm font-medium text-foreground">{title}</h3>
                {description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
            </div>
            {action && <div className="shrink-0">{action}</div>}
        </div>
        {children}
    </section>
);

/** The small grey line under a control. */
export const SettingsHint = ({ children, className }: { children: ReactNode; className?: string }) => (
    <p className={cn('mt-1.5 text-xs text-muted-foreground', className)}>{children}</p>
);

/** The grey label over a control. */
export const SettingsLabel = ({ htmlFor, children, className }: { htmlFor?: string; children: ReactNode; className?: string }) => (
    <Label htmlFor={htmlFor} className={cn('text-xs font-medium text-muted-foreground', className)}>{children}</Label>
);

/** A label, an optional "what is this" tooltip, the control, and an optional hint. */
export const SettingsField = ({
    label,
    htmlFor,
    info,
    hint,
    children,
    className,
}: {
    label?: ReactNode;
    htmlFor?: string;
    info?: string;
    hint?: ReactNode;
    children: ReactNode;
    className?: string;
}) => (
    <div className={className}>
        {label && (
            <div className="mb-1.5 flex items-center gap-1">
                <SettingsLabel htmlFor={htmlFor}>{label}</SettingsLabel>
                {info && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-flex cursor-help text-muted-foreground transition-colors hover:text-foreground">
                                <Info className="size-3.5" />
                            </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-64">{info}</TooltipContent>
                    </Tooltip>
                )}
            </div>
        )}
        {children}
        {hint && <SettingsHint>{hint}</SettingsHint>}
    </div>
);

/** An on/off setting: what it is on the left, the switch on the right. */
export const SettingsToggleRow = ({
    id,
    label,
    description,
    icon,
    checked,
    onCheckedChange,
    disabled,
    children,
}: {
    id: string;
    label: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    /** Settings that only apply while it is on, shown under it. */
    children?: ReactNode;
}) => (
    <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
                {icon && <span className="mt-0.5 flex text-muted-foreground [&_svg]:size-4">{icon}</span>}
                <div className="min-w-0">
                    <Label htmlFor={id} className="text-sm font-normal text-foreground">{label}</Label>
                    {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
                </div>
            </div>
            <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
        </div>
        {checked && children && <div className={cn('flex flex-col gap-4', icon && 'ps-7')}>{children}</div>}
    </div>
);

/**
 * A colour: a swatch to open the picker, its hex, and optionally a way back to
 * an automatic value. An empty value means "automatic" and is what gets saved.
 */
export const SettingsColorField = ({
    id,
    label,
    value,
    auto,
    autoLabel,
    hint,
    onChange,
}: {
    id: string;
    label: ReactNode;
    value?: string;
    /** The colour used when `value` is empty. Without it there is no automatic option. */
    auto?: string;
    autoLabel?: string;
    hint?: ReactNode;
    onChange: (value: string) => void;
}) => {
    const shown = value || auto || '#000000';
    return (
        <SettingsField label={label} htmlFor={id} hint={hint}>
            <div className="flex items-center gap-2">
                <Input
                    id={id}
                    type="color"
                    className="h-9 w-12 shrink-0 cursor-pointer p-1"
                    value={shown}
                    onChange={(e) => onChange(e.target.value)}
                />
                <span className="w-16 font-mono text-xs text-muted-foreground">{shown.toUpperCase()}</span>
                {auto !== undefined && autoLabel && (
                    <Button type="button" variant="ghost" size="sm" className="ms-auto" onClick={() => onChange('')} disabled={!value}>
                        {autoLabel}
                    </Button>
                )}
            </div>
        </SettingsField>
    );
};

export interface SettingsChoiceOption<T extends string> {
    value: T;
    label: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    disabled?: boolean;
}

/**
 * One of a few options that each need a line of explanation — a strategy, a
 * mode. A list of rows, the chosen one outlined; for short labels without
 * explanations use a ToggleGroup instead.
 */
export function SettingsChoiceList<T extends string>({
    value,
    options,
    onChange,
    name,
}: {
    value: T;
    options: SettingsChoiceOption<T>[];
    onChange: (value: T) => void;
    /** For the radio group's accessible name. */
    name: string;
}) {
    return (
        <div role="radiogroup" aria-label={name} className="flex flex-col gap-1.5">
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={o.disabled}
                        onClick={() => onChange(o.value)}
                        className={cn(
                            'flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                            active
                                ? SELECTED
                                : 'border-input hover:bg-muted/50',
                        )}
                    >
                        {o.icon && <span className="mt-0.5 flex text-muted-foreground [&_svg]:size-4">{o.icon}</span>}
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm text-foreground">{o.label}</span>
                            {o.description && <span className="mt-0.5 block text-xs text-muted-foreground">{o.description}</span>}
                        </span>
                        <span
                            aria-hidden
                            className={cn(
                                'mt-1 size-3.5 shrink-0 rounded-full border',
                                active ? 'border-[4px] border-foreground' : 'border-input',
                            )}
                        />
                    </button>
                );
            })}
        </div>
    );
}
